import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimatedSection from "@/components/AnimatedSection";
import { portfolioFilters, portfolioProjects } from "@/mocks/portfolioPageData";

const validFilterKeys = portfolioFilters.map((f) => f.key);

export default function PortfolioPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const categoryParam = searchParams.get("category");
  const initialFilter =
    categoryParam && validFilterKeys.includes(categoryParam)
      ? categoryParam
      : "all";

  const [activeFilter, setActiveFilter] = useState(initialFilter);

  const filteredProjects =
    activeFilter === "all"
      ? portfolioProjects
      : portfolioProjects.filter(
          (p) =>
            p.categoryKeys.includes(activeFilter) ||
            p.categoryKey === activeFilter
        );

  const handleFilter = (key: string) => {
    setActiveFilter(key);
    if (key === "all") {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ category: key }, { replace: true });
    }
  };

  useEffect(() => {
    if (categoryParam && validFilterKeys.includes(categoryParam)) {
      setActiveFilter(categoryParam);
    } else {
      setActiveFilter("all");
    }
  }, [categoryParam, validFilterKeys, setActiveFilter]);

  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Portfolio" },
        ]}
      />

      <PageHero
        badge="Our Work"
        headline="Projects We've Built"
        subtext="Explore digital products, SaaS platforms, web applications and software solutions engineered by AstroJal."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to a Laravel Expert"
      />

      {/* Portfolio Grid */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-4 font-heading tracking-tight">
                Selected Projects
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                Demonstration projects showcasing our engineering across industries. Filter by technology or solution type.
              </p>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto mt-5" />
            </div>
          </AnimatedSection>

          {/* Filters */}
          <AnimatedSection delay={100}>
            <div className="flex flex-wrap items-center justify-center gap-2.5 mb-12">
              {portfolioFilters.map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => handleFilter(filter.key)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeFilter === filter.key
                      ? "bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white shadow-md shadow-[#7C3AED]/20"
                      : "bg-background-100 border border-background-200/60 text-foreground-600 hover:border-accent-300/40 hover:text-accent-600"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </AnimatedSection>

          {/* Project Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
            {filteredProjects.map((project, i) => (
              <AnimatedSection key={project.slug} delay={i * 100}>
                <a
                  href={`/portfolio/${project.slug}`}
                  className="group block rounded-2xl overflow-hidden border border-background-200/60 hover:border-accent-300/40 hover:shadow-xl hover:shadow-accent-500/10 transition-all duration-500 card-lift bg-background-100 h-full flex flex-col"
                >
                  <div className="relative overflow-hidden h-[220px] md:h-[240px]">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-background-50/90 backdrop-blur-sm text-foreground-800 text-xs font-semibold">
                      {project.category}
                    </span>
                  </div>

                  <div className="p-6 flex flex-col flex-grow">
                    <p className="text-xs font-semibold text-accent-600 uppercase tracking-wider mb-2">
                      {project.industry}
                    </p>
                    <h3 className="text-lg font-bold text-foreground-950 mb-2.5 font-heading">
                      {project.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-5 flex-grow">
                      {project.description}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-5">
                      {project.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-1 rounded-md bg-background-50 border border-background-200/60 text-foreground-600 text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-600 group-hover:text-accent-700 transition-colors btn-arrow-hover">
                      View Case Study
                      <i className="ri-arrow-right-line btn-arrow text-xs transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </a>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <PageCTA
        heading="Want Results Like These?"
        text="Let's build something that scales."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to a Laravel Expert"
      />
    </PageLayout>
  );
}